import { z } from 'zod'
import { siteVisitDtoSchema, type SiteVisitDto } from '@lib/dto/site-visit.dto'
import type { VisitKind } from '@lib/enums/visit-kind.enum'
import logger, { toErrorMessage } from '@/helpers/logger'

/*
 * A site's visits as this phone knows them: the server's list, and any visit
 * started here that has not reached it yet. See docs/site-visits.md.
 *
 * Kept per site in local storage, like the surveys, because a visit is
 * started standing in a park: it has to exist, with an id and a day, before
 * there is any signal, and the surveys put what is found into it straight
 * away. The server is told when it can be, and may answer that another phone
 * started the same day's visit first, in which case this one is joined into
 * it and the phone carries on under the kept id.
 */

/**
 * A visit as this phone stores it: the fields it shares with `SiteVisitDto`,
 * plus whether the server has acknowledged it yet.
 */
export type LocalVisit = Pick<
  SiteVisitDto,
  | 'id'
  | 'siteId'
  | 'startedOn'
  | 'coversTrees'
  | 'coversIrrigation'
  | 'finishedAt'
  | 'startedByName'
  | 'joinedIds'
> & {
  /** Started (or widened) here, and not yet acknowledged by the server. */
  pending: boolean
}

/**
 * Parsed with the visit DTO's own fields, so a day, an id, or a finish time
 * this phone keeps cannot be a shape the server would refuse. The two names
 * an older build left out still default, or a visit written before them
 * would be dropped.
 */
const localVisitSchema = siteVisitDtoSchema
  .pick({
    id: true,
    siteId: true,
    startedOn: true,
    coversTrees: true,
    coversIrrigation: true,
    finishedAt: true,
  })
  .extend({
    startedByName: siteVisitDtoSchema.shape.startedByName.default(null),
    joinedIds: siteVisitDtoSchema.shape.joinedIds.default([]),
    pending: z.boolean(),
  })

/**
 * The parsed visit is exactly `LocalVisit`. Defaults make the input wider
 * than the output (an older record may omit the two names), so this locks
 * the output rather than the input.
 */
type LocalVisitOutput = z.output<typeof localVisitSchema>
const _localVisitMatches: LocalVisitOutput extends LocalVisit
  ? LocalVisit extends LocalVisitOutput
    ? true
    : never
  : never = true
void _localVisitMatches

export interface VisitState {
  visits: LocalVisit[]
}

export const EMPTY_VISITS: VisitState = { visits: [] }

function storageKey(siteId: string): string {
  return `swocrm.visits.${siteId}.v1`
}

/**
 * Read a site's visits. Each is parsed on its own, so one an older build
 * wrote costs that one: a pending visit dropped is a visit the server never
 * hears of, which the surveys would then refuse to send anything for, so the
 * loss is logged rather than swallowed.
 */
export function loadVisits(siteId: string): VisitState {
  let raw: string | null = null
  try {
    raw = window.localStorage.getItem(storageKey(siteId))
  } catch (caught) {
    logger.error('visits: could not read the stored visits', {
      siteId,
      reason: toErrorMessage(caught),
    })
    return EMPTY_VISITS
  }
  if (raw === null) return EMPTY_VISITS
  const visits: LocalVisit[] = []
  for (const candidate of parseEnvelope(raw)) {
    const parsed = localVisitSchema.safeParse(candidate)
    if (parsed.success) {
      visits.push(parsed.data)
    } else {
      logger.error('visits: dropping a stored visit this build cannot read', {
        siteId,
      })
    }
  }
  return { visits }
}

/** The stored list, each entry still to be judged on its own. */
const envelopeSchema = z.object({ visits: z.array(z.unknown()) })

function parseEnvelope(raw: string): z.infer<typeof envelopeSchema>['visits'] {
  try {
    const parsed = envelopeSchema.safeParse(JSON.parse(raw))
    return parsed.success ? parsed.data.visits : []
  } catch {
    return []
  }
}

export function saveVisits(siteId: string, state: VisitState): void {
  try {
    window.localStorage.setItem(storageKey(siteId), JSON.stringify(state))
  } catch (caught) {
    logger.error('visits: could not save the visits', {
      siteId,
      reason: toErrorMessage(caught),
    })
  }
}

/** Today on this phone, as the calendar day a visit starts on. */
export function todayLocal(now: Date = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${String(now.getFullYear())}-${month}-${day}`
}

/**
 * Which kinds of work a visit covers. Keyed by {@link VisitKind}, so a new
 * kind has to say whether this visit includes it.
 */
export type VisitCovers = Record<VisitKind, boolean>

export function covers(visit: LocalVisit, kind: VisitKind): boolean {
  return kind === 'trees' ? visit.coversTrees : visit.coversIrrigation
}

/** Newest first, by the day started; one per day, so the day is enough. */
function newestFirst(visits: readonly LocalVisit[]): LocalVisit[] {
  return [...visits].sort((a, b) => b.startedOn.localeCompare(a.startedOn))
}

/** Every visit that covers this kind of work, newest first. */
export function visitsOf(state: VisitState, kind: VisitKind): LocalVisit[] {
  return newestFirst(state.visits).filter((visit) => covers(visit, kind))
}

/** The visit a survey of this kind works in: the newest that covers it. */
export function currentVisit(
  state: VisitState,
  kind: VisitKind,
): LocalVisit | null {
  return newestFirst(state.visits).find((visit) => covers(visit, kind)) ?? null
}

/**
 * The visit before `visit` that looked at this kind of work: the newest one
 * started on an earlier day. Its findings are what a revisit shows as "Last
 * visit" (docs/site-visits.md). One the server has not heard of yet has
 * nothing to read back.
 */
export function previousVisit(
  state: VisitState,
  kind: VisitKind,
  visit: Pick<LocalVisit, 'startedOn'>,
): LocalVisit | null {
  return (
    newestFirst(state.visits).find(
      (candidate) =>
        covers(candidate, kind) &&
        !candidate.pending &&
        candidate.startedOn < visit.startedOn,
    ) ?? null
  )
}

/** Whether two ids name one visit: the same id, or one joined into the other. */
export function isSameVisit(
  visit: Pick<LocalVisit, 'id' | 'joinedIds'>,
  id: string,
): boolean {
  return visit.id === id || visit.joinedIds.includes(id)
}

/**
 * Start a visit today, or widen today's to these kinds of work.
 *
 * One visit per site per day, the rule the server holds too: a second "Start
 * a visit" on the same day is the same visit, covering more. A widened visit
 * is pending again, so the server hears what it now covers.
 */
export function startVisit(
  state: VisitState,
  siteId: string,
  kinds: VisitCovers,
  today: string,
  newId: () => string = () => crypto.randomUUID(),
): { state: VisitState; visit: LocalVisit } {
  const existing = state.visits.find((visit) => visit.startedOn === today)
  if (existing !== undefined) {
    const widened: LocalVisit = {
      ...existing,
      coversTrees: existing.coversTrees || kinds.trees,
      coversIrrigation: existing.coversIrrigation || kinds.irrigation,
    }
    const changed =
      widened.coversTrees !== existing.coversTrees ||
      widened.coversIrrigation !== existing.coversIrrigation
    const visit = changed ? { ...widened, pending: true } : existing
    return {
      state: {
        visits: state.visits.map((candidate) =>
          candidate === existing ? visit : candidate,
        ),
      },
      visit,
    }
  }
  const visit: LocalVisit = {
    id: newId(),
    siteId,
    startedOn: today,
    coversTrees: kinds.trees,
    coversIrrigation: kinds.irrigation,
    finishedAt: null,
    startedByName: null,
    joinedIds: [],
    pending: true,
  }
  return { state: { visits: [...state.visits, visit] }, visit }
}

/** The server's record of a visit, as the phone keeps it. */
export function fromServer(visit: SiteVisitDto): LocalVisit {
  return {
    id: visit.id,
    siteId: visit.siteId,
    startedOn: visit.startedOn,
    coversTrees: visit.coversTrees,
    coversIrrigation: visit.coversIrrigation,
    finishedAt: visit.finishedAt,
    startedByName: visit.startedByName,
    joinedIds: visit.joinedIds,
    pending: false,
  }
}

/**
 * The server's record of a visit, unless this phone has since widened it to
 * a kind the server's does not cover: then the wider one, still pending, to
 * be sent again.
 */
function confirm(
  server: LocalVisit,
  local: LocalVisit | undefined,
): LocalVisit {
  if (local === undefined || !local.pending) return server
  const coversTrees = server.coversTrees || local.coversTrees
  const coversIrrigation = server.coversIrrigation || local.coversIrrigation
  const wider =
    coversTrees !== server.coversTrees ||
    coversIrrigation !== server.coversIrrigation
  return wider
    ? { ...server, coversTrees, coversIrrigation, pending: true }
    : server
}

/**
 * Fold the server's list of a site's visits in: its visits are the truth,
 * plus any started here that it has not heard of yet. A visit the server has
 * dropped (archived from a desk) goes.
 */
export function mergeServerList(
  state: VisitState,
  server: readonly SiteVisitDto[],
): VisitState {
  return mergeKnown(state, server.map(fromServer))
}

/**
 * The same, from visits the server has acknowledged as the phone kept them:
 * the copy stored for a cold start with no signal, before the list arrives.
 */
export function mergeKnown(
  state: VisitState,
  known: readonly LocalVisit[],
): VisitState {
  const pending = state.visits.filter((visit) => visit.pending)
  return {
    visits: [
      ...known.map((visit) =>
        confirm(
          visit,
          pending.find((local) => isSameVisit(visit, local.id)),
        ),
      ),
      ...pending.filter(
        (local) => !known.some((visit) => isSameVisit(visit, local.id)),
      ),
    ],
  }
}

/**
 * Fold in the server's answer to starting one visit. The answer may be a
 * different visit from the one sent: another phone's, started the same day,
 * that this one was joined into. Either way it replaces the one sent.
 */
export function acknowledge(
  state: VisitState,
  sentId: string,
  server: SiteVisitDto,
): VisitState {
  const confirmed = confirm(
    fromServer(server),
    state.visits.find((visit) => visit.id === sentId),
  )
  return {
    visits: [
      ...state.visits.filter(
        (visit) => visit.id !== sentId && !isSameVisit(confirmed, visit.id),
      ),
      confirmed,
    ],
  }
}
